import '@gig/pp-checkout';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import * as Sentry from '@sentry/browser';

import './header.js';
import './footer.js';
import './pp-policy.js';
import './extra-info.js';
import './pp-feedback.js';
import './pp-skeleton.js';
import './certification.js';
import './loading-spinner.js';
import { globalStyles, skeletonStyles } from '../global-styles.js'
import { setLocale } from '../locale/localization.js';
import { IOrderInfo } from '../types/order.js';
import { IMerchantCustomizeConfig } from '../types/customSettings.js';
import { matchedLocaleMap } from '../locale/matchLocale.js';
import { allLocales, sourceLocale } from '../generated/locale-codes.js';
import { CHECKOUT_FAILED_EVENT } from '../constant.js';
import { ELogLabel, triggerManualLog } from '../utils/log.js';

@customElement('pp-cashier')
export class PpCashier extends LitElement {
  static styles = [
    globalStyles,
    skeletonStyles,
    css`
      :host {
        display: block;
        color: var(--pp-primary-text-color);
        font-family: var(--pp-checkout-font-family);
        background-color: var(--pp-body-background, rgb(250, 250, 250));
      }

      .pp-checkout-container {
        margin: 0 auto;
        max-width: 36rem;
        padding-top: 24px;
      }

      .pp-checkout-container {
        display: flex;
        min-height: calc(100vh - 48px);
        padding-top: 1.5rem;
        padding-bottom: 1rem;
        flex-direction: column;
      }
      
      pp-checkout {
        margin: -12px;
      }

      .pp-checkout-core {
        min-height: 479px;
        overflow: hidden;
      }

      pp-discount {
        display: block;
      }

      @media only screen and (min-width: 0) and (max-width: 576px){
        .pp-checkout-container {
          padding-top: 0;
          padding-bottom: 0;
        }

        pp-checkout {
          margin: 0;
          flex-grow: 1;
          border-radius: 0;
          min-height: unset;
        }

        .skeleton {
          padding: 0 12px;
        }

        pp-discount {
          display: none;
        }
  
        pp-certification {
          margin-top: auto;
        }
      }
    `
  ];

  @state() private locale: string = '';

  @state() private _orderInfo!: null | IOrderInfo;

  @state() private _cancelUrl: string = '';

  @state() private _customizeConfig: Partial<IMerchantCustomizeConfig> = {};

  @state() private _fulfilled: boolean = false;

  @state() private checkoutFailedCount: number = 0;
  
  @state() private timeId?: number;

  @state() private pageStayTime: number = 0;
  
  @state() private discountDescription: string | undefined;

  @state()private visibilityChangeHandler?: () => void;

  /**
   * 初始化Sentry
   */
  initSentry() {
    try {
      const { VITE_ENV, VITE_SENTRY_DSN } = import.meta.env;
    
      if(VITE_ENV !== 'DEV') {
        Sentry.init({
          dsn: VITE_SENTRY_DSN,
          environment: VITE_ENV,
          integrations: [
            // 错误追踪，开启
            Sentry.browserTracingIntegration(),
          ],
          tracesSampleRate: 1.0,
          tracePropagationTargets: ['localhost', /^https:\/\/pingpongx.com/],
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0
        });
      }
    } catch (error) {
      console.error(error)
    }
  }

  // 生命周期方法 ================================================================

  connectedCallback(): void {
    this.initSentry();
    super.connectedCallback();
    window.addEventListener(CHECKOUT_FAILED_EVENT, this.handleCheckoutFailed.bind(this))
    this.startVisibilityAwareTimer();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(CHECKOUT_FAILED_EVENT, this.handleCheckoutFailed.bind(this))
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler!);
  }

  // Getter方法 ================================================================

  /**
   * 根据商户配置动态设置额外信息模块样式
   */
  get extraStyle() {
    const customizeStyles = this._orderInfo?.storeCustomizeInfo?.customizeStyles;
    const map = {
      left: 'justify-between',
      center: 'justify-center',
      right: 'justify-between flex-direction',
    }
    return customizeStyles?.returnPosition ? map[customizeStyles.returnPosition] : map.left;
  }

  // 事件处理 ===================================================================

  /**
   * 支付核心包获取订单信息后执行
   */
  private _handleDispatch(customEvent: CustomEvent<IOrderInfo>) {
    const { detail } = customEvent;
    this._customizeConfig = window.PingPong?.Checkout?.customizeConfig ?? {};
    this._renderShortcutIcon();
    this._orderInfo = detail;
  }

  /**
   * 支付核心包支付失败后执行
   */
  private handleCheckoutFailed() {
    this.checkoutFailedCount += 1;
  }

  /**
   * 基于页面可见性的计时逻辑
   */
  private startVisibilityAwareTimer() {
    // 页面隐藏时暂停计时
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.clearTimer();
      } else {
        this.resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    this.resumeTimer();
  }

  /**
   * 单次定时器, >= 10s时停止计时
   */
  private resumeTimer() {
    if(this.pageStayTime >=10 ) {
      return this.clearTimer();
    };

    const startTime = Date.now();
    const updateTime = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      this.pageStayTime = Math.min(elapsed, 10); // 最大记录10秒

      if (this.pageStayTime < 10 && !document.hidden) {
        this.timeId = window.setTimeout(updateTime, 1000);
      }
    };

    this.timeId = window.setTimeout(updateTime, 1000);
  }

  /**
   * 清除定时器
   */
  private clearTimer() {
    if (this.timeId) {
      clearTimeout(this.timeId);
      this.timeId = undefined;
    }
  }

  /**
   * 是否显示反馈模块: 
   * (连续支付失败2次 || 收银台页面停留超过10s) && 该 accId 下配置开启
   */
  getIsShowFeedback() {
    const { orderId, displayCashierTool } = this._orderInfo || {};
    return !!orderId && !!displayCashierTool && (this.checkoutFailedCount >= 2 || this.pageStayTime >= 10);
  }

  private _localeChange(customEvent: CustomEvent<string>) {
    const { detail } = customEvent;
    this._setLocale(detail);
    document.body.style.direction = this.locale === 'ar'? 'rtl': 'ltr';
    triggerManualLog(ELogLabel.TRIGGER_LANGUAGE, detail);
  }

  private async _setLocale(locale: string) {
    this.locale = locale;
    await setLocale(locale);
  }

  /**
   * 设置收银台语言
   * 1. 根据订单传入的 language 设置当前语言
   * 2. 没取到时, 获取浏览器默认语言
   */
  private _setCashierLanguage() {
    if(!this._orderInfo) return;
    const { language } = this._orderInfo;
    // 匹配收银台支持语言
    const matchLocale = matchedLocaleMap[language] || matchedLocaleMap[navigator.language];
    const locale = allLocales.includes(matchLocale)? matchLocale: sourceLocale;
    this._setLocale(locale);
    this._fulfilled = true;
  }

  /**
   * 渲染商户定制网页快捷图标
   * @param iconPath - 图标文件的路径
   */
  private _renderShortcutIcon(): void {
    const head: HTMLHeadElement | null = document.head;
    
    const { browserTabIconUrl, tabTitle } = this._customizeConfig;

    if(browserTabIconUrl) {
      const link: HTMLLinkElement = document.createElement('link');
      link.rel = 'favicon icon';
      link.href = browserTabIconUrl;
      head.appendChild(link);
    }

    if(tabTitle) {
      document.title = tabTitle;
    }

  }

  private _getCancelUrl() {
    let { shopperCancelUrl, shopperResultUrl } = this._orderInfo ?? {};
    let _url = shopperCancelUrl || shopperResultUrl || '';
    if(!_url) return;
    const separator = _url.includes('?') ? '&' : '?';
    this._cancelUrl = `${_url}${separator}type=cancel`;
  }

  private _setDiscountDescription() {
    let { cashierActivityBanner } = this._orderInfo ?? {};

    this.discountDescription = cashierActivityBanner;
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('_orderInfo')) {
      this._getCancelUrl();
      this._setCashierLanguage();
      this._setDiscountDescription();
    }
  }
  
  private _renderFooter() {
    const { 
      returnBtnText,
      displayPCI = false, 
      displayReturnBtn = false, 
    } = this._customizeConfig;

    const displayReturn = !!this._cancelUrl && displayReturnBtn;

    const returnBtn = displayReturn? html`
      <pp-cashier-extra 
        cancelUrl=${this._cancelUrl}
        returnBtnText=${ifDefined(returnBtnText)}
      ></pp-cashier-extra>
    `: ''

    const feedback = this.getIsShowFeedback() ? html`
      <pp-feedback orderId=${this._orderInfo!.orderId}></pp-feedback>
    `: '';

    const pci = displayPCI? html`
      <pp-certification .displayReturn=${displayReturn}></pp-certification>
    `: ''

    const footer = html`
      <pp-cashier-footer 
        locale=${this.locale} 
        cancelUrl=${this._cancelUrl}
        .customizeConfig=${this._customizeConfig}
        @locale-changed=${this._localeChange}>
      </pp-cashier-footer>
    `

    const policy = html`<pp-policy></pp-policy>`

    return html`
      <div class="flex items-center gap-2 ${this.extraStyle}">
        ${returnBtn}
        ${feedback}
      </div>
      ${pci}
      ${footer}
      ${policy}
    `
  }

  private _renderDiscountBanner() {
    return this.discountDescription
      ? html`<pp-discount description=${this.discountDescription}></pp-discount>`
      : ''
  }

  render() {
    

    return html`
      <div class="pp-cashier">
        ${this._renderDiscountBanner()}
        <pp-cashier-header
          locale=${this.locale}
          .fulfilled=${this._fulfilled}
          .customizeConfig=${this._customizeConfig}
          @locale-changed=${this._localeChange}
        ></pp-cashier-header>
        <div class="pp-checkout-container">
          <div class="pp-checkout-core">
            ${
              !this._fulfilled? html`<pp-skeleton></pp-skeleton>`: ''
            }
            <pp-checkout 
              locale="${this.locale}" 
              accessToken=${window.__TOKEN__}
              @dispatch-order="${this._handleDispatch}"
              @pp-checkout-failed=${this.handleCheckoutFailed}
              class=${this._fulfilled? '': 'hidden'}
            ></pp-checkout>
          </div>

          <div class="m-t-auto">
            ${
              !this._fulfilled? html`
                <div class="skeleton">
                  <div class="LOADING-payment-header LOADING-loads"></div>
                  <div class="LOADING-payment-row LOADING-loads"></div>
                </div>
              `: this._renderFooter()
            }
          </div>
        </div>
      </div>
    `;
  }
}
